/**
 * 流水「批量选中合计条」—— **唯一实现**（C 域 §5.1.9 · `AC-C1.24` 必测）
 *
 * 依据：C 域 PRD **§5.1.9**（合计条）· **AC-C1.24** · **§5.1.8**（跨币种展示：不折算）· `DR-004`。
 *
 * ## 目标形态（`AC-C1.24` 原文）
 *
 * ```
 * 已选 5 笔 · 流入 ¥21,000.00 · 流出 ¥3,750.00 · 另有 ¥8,600.00 不计收支
 * ```
 *
 * ## 🔴 四条口径，每一条都对应一种"以为对但其实错"的写法
 *
 * | 口径 | 为什么容易写错 |
 * |---|---|
 * | **笔数计全部选中交易** | 直觉写法是"有金额的笔数"—— 但一笔全是不计收支腿的交易**也是选中的一笔**，漏掉会让人以为自己少选了 |
 * | **只统计「计入收支」的腿** | 直接用 `transactions` 的金额？**交易根本不持有金额**（架构 §4.1）。只看 `direction`？会把转账的两腿当成"一进一出"，在合计里**显得收支各有几千**，而实际上它一分钱都没花 |
 * | 🔴 **按方向分列、不做净额** | 净额会把「流入 21,000 / 流出 3,750」压成「+17,250」—— 用户看到的是一个**没有任何真实含义**的数字（`AC-C1.24` 明禁） |
 * | 🔴 **按币种分组、不折算** | 跨币种相加违 `DR-004`；且"筛 ≥100：人民币比 100 元、美元比 100 美元"是既定口径（§5.1.8） |
 *
 * ## 为什么也必须是纯函数
 *
 * 合计条是**纯前端交互**（选中态一变就要重算），它天然长在端侧 —— 但「金额聚合不得在 app 包实现」
 * 是阻断级架构规则（`no-aggregate-in-apps`，架构 §5.2-4）。两条约束的交集只有一个解：
 * **逻辑放 `nucleus/`，端侧只是调用它。** 这也顺带保证了 Web 与小程序勾出来的数是同一个数。
 */

import type { Cents } from '../types/brand.js';
import { toCents } from './money.js';
import { isCountedInStats, type RowLeg } from './txn-row.js';

/** 参与合计的一次选中（只需腿 —— 交易级的「不计收支」标记已在腿的有效值里考虑） */
export interface SelectionTxn {
  id: string;
  legs: readonly RowLeg[];
}

/** 单一币种的合计 */
export interface CurrencySummary {
  /** ISO 4217 */
  currency: string;
  /** 计入收支的 `in` 腿合计 */
  inflow: Cents;
  /** 计入收支的 `out` 腿合计 */
  outflow: Cents;
  /**
   * **不计收支**的腿合计（单列提示，**不进 `inflow` / `outflow`，也不与它们相减**）。
   *
   * ⚠️ 语义是"这批里还有这么多钱在动，但它不是收支"—— 转账、分期每期还款、本金还款、
   * 工资条的社保 / 公积金转账腿都落这里（C 域 §5.1.9「防『算漏了』的误读」）。
   *
   * 🔴 **统计规则见 {@link summarizeSelection} 说明 —— 不是"把所有不计收支的腿加起来"**。
   */
  excluded: Cents;
}

/** 合计条结果 */
export interface SelectionSummary {
  /** 🔴 **全部**选中交易数（含"全是不计收支腿"的那些） */
  transactionCount: number;
  /**
   * 逐币种一行 —— **顺序按币种字典序**。
   *
   * 🔴 排序不是装饰：合计条会随每次勾选重算，顺序不稳定会让数字"跳来跳去"。
   * 只出**至少有一条腿**的币种（零腿币种不存在）。
   */
  byCurrency: CurrencySummary[];
  /** 是否存在不计收支的腿（UI 据此决定要不要渲染「另有 … 不计收支」那一段） */
  hasExcluded: boolean;
}

/**
 * 汇总一批选中的交易。**唯一入口**（Web 与小程序共用一个实现）。
 *
 * ## 🔴 「不计收支」这一段为什么**不能**把腿直接加起来
 *
 * 一笔 3,600 元的转账有**两条**腿（转出 3,600 + 转入 3,600）。若照直累加，合计条会显示
 * 「另有 ¥7,200.00 不计收支」—— 而用户刚刚转出去的是 3,600。把这个数写错，比不显示更糟：
 * 它会让人怀疑整条合计条的每一格。
 *
 * ⇒ 规则：**逐交易、逐币种**地取「不计收支的流出合计」与「不计收支的流入合计」中的**较大者**。
 *
 * | 形态 | 计算 | 结果 |
 * |---|---|---|
 * | 转账（转出 3,600 / 转入 3,600） | `max(3,600, 3,600)` | 3,600 ✓ 只计一次 |
 * | 分期每期还款（银行出 / 信用卡入） | `max(800, 800)` | 800 ✓ |
 * | 工资条的社保 / 公积金转账腿（工资卡出 / 社保账户入） | `max(1,600, 1,600)` | 1,600 ✓ |
 * | 待摊一次性付款（**单腿流出**，不计收支） | `max(5,000, 0)` | 5,000 ✓ |
 * | 「单位缴纳」（**单腿流入**，`DR-016` §3.4 企业缴费） | `max(0, 1,200)` | 1,200 ✓ |
 *
 * 两侧理论相等（同币种转账），取 `max` 是**保守且可解释**的收口：跨币种时两侧金额本就不同
 * （`DR-004` 隐含汇率不存储），取较大者保证不会把规模报小。
 *
 * @param txns 选中的交易（各自带全部腿）
 */
export function summarizeSelection(txns: readonly SelectionTxn[]): SelectionSummary {
  interface Acc {
    inflow: number;
    outflow: number;
    excluded: number;
  }
  const acc = new Map<string, Acc>();
  let hasExcluded = false;

  const bucket = (currency: string): Acc => {
    let a = acc.get(currency);
    if (a === undefined) {
      a = { inflow: 0, outflow: 0, excluded: 0 };
      acc.set(currency, a);
    }
    return a;
  };

  for (const txn of txns) {
    // 本交易、本币种下「不计收支」的两侧各自合计 —— 走出循环再取 max（见方法说明）
    const excl = new Map<string, { in: number; out: number }>();
    for (const leg of txn.legs) {
      const a = bucket(leg.currency);
      const amount = leg.amount as number;
      if (isCountedInStats(leg)) {
        if (leg.direction === 'in') a.inflow += amount;
        else a.outflow += amount;
        continue;
      }
      hasExcluded = true;
      let e = excl.get(leg.currency);
      if (e === undefined) {
        e = { in: 0, out: 0 };
        excl.set(leg.currency, e);
      }
      if (leg.direction === 'in') e.in += amount;
      else e.out += amount;
    }
    for (const [currency, e] of excl) {
      bucket(currency).excluded += Math.max(e.in, e.out);
    }
  }

  const byCurrency: CurrencySummary[] = [...acc.entries()]
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    .map(([currency, a]) => ({
      currency,
      inflow: toCents(a.inflow),
      outflow: toCents(a.outflow),
      excluded: toCents(a.excluded),
    }));

  return { transactionCount: txns.length, byCurrency, hasExcluded };
}
